import { IconArrowDown, IconArrowUp } from '../Icons';
import { Sparkline } from '../charts/Sparkline';
import { signedPercent } from '../../lib/format';

interface DeltaProps {
  value: number;
  /** For crash-free rate a fall is bad; for churn a fall is good. */
  invert?: boolean;
  suffix?: string;
}

export function Delta({ value, invert = false, suffix = 'vs prior period' }: DeltaProps) {
  const flat = Math.abs(value) < 0.001;
  const good = invert ? value < 0 : value > 0;
  const cls = flat ? 'delta--flat' : good ? 'delta--up' : 'delta--down';
  return (
    <span className={`delta ${cls}`}>
      {flat ? null : value > 0 ? <IconArrowUp size={11} /> : <IconArrowDown size={11} />}
      {signedPercent(value)}
      {suffix ? <span className="delta__suffix"> {suffix}</span> : null}
    </span>
  );
}

interface Props {
  label: string;
  value: string;
  delta?: number;
  /** Set when a rise in this metric is bad news. */
  invertDelta?: boolean;
  history?: number[];
  sparkColor?: string;
  foot?: string;
}

export function StatTile({ label, value, delta, invertDelta, history, sparkColor, foot }: Props) {
  return (
    <article className="card tile">
      <span className="tile__label">{label}</span>
      <div className="tile__row">
        <span className="tile__value">{value}</span>
        {history && history.length > 1 ? (
          <Sparkline values={history} color={sparkColor ?? 'var(--accent-strong)'} />
        ) : null}
      </div>
      {delta !== undefined ? <Delta value={delta} invert={invertDelta} /> : null}
      {foot ? <span className="tile__foot">{foot}</span> : null}
    </article>
  );
}

interface HeroProps {
  label: string;
  value: string;
  delta: number;
  meta: React.ReactNode;
  history: number[];
}

export function HeroTile({ label, value, delta, meta, history }: HeroProps) {
  return (
    <article className="card hero">
      <span className="hero__label">{label}</span>
      <span className="hero__value">{value}</span>
      <div className="hero__meta">
        <Delta value={delta} />
        {meta}
      </div>
      <div style={{ marginTop: 'auto', paddingTop: 'var(--sp-3)' }}>
        {/* One data point is not a trend. Saying so beats leaving the tallest
            card on the page blank, which reads as a failed load. */}
        {history.length > 1 ? (
          <Sparkline values={history} width={300} height={54} color="var(--accent-strong)" />
        ) : (
          <span className="tile__foot">Not enough history to chart at this range.</span>
        )}
      </div>
    </article>
  );
}
